import { useState } from 'react';
import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';

export const TextInputRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onCancel,
  isSubmitting,
  compact,
}) => {
  const [value, setValue] = useState(response.defaultValue || '');
  const isRichText = response.kind === 'rich_text';
  const isMultiline = response.multiline !== false; // Default to multiline

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Cmd/Ctrl + Enter for multiline, Enter for single line
    if (e.key === 'Enter') {
      if (!isMultiline || (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    }
    // Cancel on Escape
    if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  return (
    <div className={compact ? hitlStyles.containerCompact : hitlStyles.container}>
      {response.message && (
        <p className={hitlStyles.message}>{response.message}</p>
      )}
      
      {response.context && (
        <p className={hitlStyles.context}>{response.context}</p>
      )}

      <div className="mb-3">
        {isMultiline ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={response.placeholder || 'Type your answer...'}
            rows={isRichText ? 6 : 3}
            disabled={isSubmitting}
            className={hitlStyles.textarea}
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={response.placeholder || 'Type your answer...'}
            disabled={isSubmitting}
            className={hitlStyles.input}
            autoFocus
          />
        )}
      </div>

      <div className="flex gap-2">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={!value.trim() || isSubmitting}
          className={cn(hitlStyles.actionButton, hitlStyles.actionButtonPrimary, 'flex-1')}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className={hitlStyles.spinner} />
              Processing...
            </span>
          ) : (
            'Submit'
          )}
        </button>
      </div>

      {isMultiline && (
        <p className="text-xs text-gray-500 mt-2">
          Press {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to submit
        </p>
      )}
    </div>
  );
};

